import { type FC, type MouseEvent, type ReactNode, useState } from "react";

import { classNames, Icon, Link, Text } from "@alpacahq/ui/old";

import { cn } from "~/v2/utils/cn";
export type FAQItem = {
  question: string;
  answer: ReactNode;
};

interface FAQItemState extends FAQItem {
  id: string;
  isActive: boolean;
}

type FAQProps = {
  title?: ReactNode;
  faq: FAQItem[];
  hideContactUs?: boolean;
  titleClassName?: string;
  itemClassName?: string;
};

export const FAQ: FC<FAQProps> = ({
  title = "Frequently Asked Questions",
  faq: faqProp,
  hideContactUs = false,
  titleClassName,
  itemClassName,
}) => {
  const [faq, setFaq] = useState<FAQItemState[]>(
    faqProp.map((item, index) => ({
      ...item,
      id: index.toString(),
      isActive: false,
    })),
  );

  const handleQuestionClick = (id: string) => () => {
    setFaq((prevFaq) =>
      prevFaq.map((item) => ({
        ...item,
        isActive: item.id === id ? !item.isActive : item.isActive,
      })),
    );
  };

  // Let users click within the answer without closing it.
  const handleClickAnswer = (event: MouseEvent) => event.stopPropagation();

  return (
    <>
      <Text
        className={classNames("mb-4 font-semibold text-lg", titleClassName)}
      >
        {title}
      </Text>

      {!hideContactUs && (
        <div className="mb-6 flex gap-2">
          <Icon name="ChatBubbleLeftRight" className="text-yellow-500" />
          <Text className="flex">Still need help?</Text>
          <Link href="mailto:support@alpaca.markets">Contact Us</Link>
        </div>
      )}

      <ul>
        {faq.map(({ id, question, answer, isActive }) => (
          <li
            key={id}
            className={classNames(
              "mb-4 cursor-pointer items-center justify-between rounded-lg bg-gray-50 p-6",
              itemClassName,
            )}
            onClick={handleQuestionClick(id)}
          >
            <div className="flex gap-3">
              <Icon
                className="mt-1 text-sm"
                name={isActive ? "ChevronUp" : "ChevronDown"}
              />
              <Text className="block font-medium text-md leading-relaxed">
                {question}
              </Text>
            </div>

            <div
              className={cn(
                "ml-8 overflow-hidden font-light duration-600 ease-in-out",
                isActive ? "max-h-screen" : "max-h-0",
              )}
              onClick={handleClickAnswer}
            >
              <div className="mt-2">{answer}</div>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
};
